import { IMenuProduct } from '@/_types';
import { AppDispatch } from '@/_store/index';
import { EXCLUDE, STOP } from '@/_store/cart/cart.slice';
import { ICartItem } from '@/_store/types';

export type MenuProductUnavailabilityReason =
  | 'missing'
  | 'inactive'
  | 'schedule';

type MenuProductsInput = IMenuProduct[] | { products?: IMenuProduct[] };

function normalizeMenuProducts(
  menuProducts: MenuProductsInput,
): IMenuProduct[] {
  if (Array.isArray(menuProducts)) {
    return menuProducts;
  }
  return menuProducts?.products ?? [];
}

function parseScheduleDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getScheduleEvaluationDate(
  futureTime: Date | string | null | undefined,
): Date {
  if (!futureTime) return new Date();
  const parsed =
    futureTime instanceof Date ? futureTime : new Date(futureTime);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

export function getMenuProductUnavailabilityReason(
  menuProduct: IMenuProduct | undefined,
  opts?: { at?: Date },
): MenuProductUnavailabilityReason | null {
  if (!menuProduct) {
    return 'missing';
  }

  if (menuProduct.isActive === false) {
    return 'inactive';
  }

  const fromRaw = menuProduct.availableFromTime?.trim() ?? '';
  const toRaw = menuProduct.availableToTime?.trim() ?? '';
  if (fromRaw && toRaw) {
    const start = parseScheduleDate(fromRaw);
    const end = parseScheduleDate(toRaw);
    if (start && end) {
      const at = opts?.at ?? new Date();
      if (at < start || at > end) {
        return 'schedule';
      }
    }
  }

  return null;
}

export function getFirstCartMenuAvailabilityIssue(
  cart: ICartItem[],
  menuProducts: MenuProductsInput,
  opts?: { at?: Date },
): {
  product_id: string;
  reason: MenuProductUnavailabilityReason;
} | null {
  const products = normalizeMenuProducts(menuProducts);
  const at = opts?.at;

  for (const product of cart) {
    const menuProduct = products.find(
      (item) => item.productId === product.product_id,
    );
    const reason = getMenuProductUnavailabilityReason(menuProduct, { at });
    if (reason) {
      return {
        product_id: product.product_id,
        reason,
      };
    }
  }

  return null;
}

export function syncCartStopFlagsFromMenuProducts(
  dispatch: AppDispatch,
  cart: ICartItem[],
  menuProducts: MenuProductsInput,
  opts: { shipper_id: string; at?: Date },
): void {
  const products = normalizeMenuProducts(menuProducts);
  const { shipper_id, at } = opts;

  for (const product of cart) {
    const menuProduct = products.find(
      (item) => item.productId === product.product_id,
    );
    const reason = getMenuProductUnavailabilityReason(menuProduct, { at });

    if (reason && !product.in_stop) {
      dispatch(
        STOP({ shipper_id, product_id: product.product_id }),
      );
    } else if (!reason && product.in_stop) {
      dispatch(
        EXCLUDE({ shipper_id, product_id: product.product_id }),
      );
    }
  }
}
